import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Store, Grid3x3, Package, Tag, Ticket, Newspaper, Calendar,
  Mountain, Briefcase, Home, Building2, Image, Palette, ToggleRight, Settings,
  LogOut, Menu, X, ExternalLink, HeartHandshake, Clapperboard, Gift,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/r/businesses", label: "Empresas", icon: Store },
  { to: "/admin/r/services", label: "Serviços", icon: HeartHandshake },
  { to: "/admin/r/categories", label: "Categorias", icon: Grid3x3 },
  { to: "/admin/r/products", label: "Produtos", icon: Package },
  { to: "/admin/r/promotions", label: "Promoções", icon: Tag },
  { to: "/admin/r/coupons", label: "Cupons", icon: Ticket },
  { to: "/admin/r/news", label: "Notícias", icon: Newspaper },
  { to: "/admin/r/events", label: "Eventos", icon: Calendar },
  { to: "/admin/r/tourist_spots", label: "Pontos Turísticos", icon: Mountain },
  { to: "/admin/r/jobs", label: "Vagas", icon: Briefcase },
  { to: "/admin/r/rentals", label: "Casas para Alugar", icon: Home },
  { to: "/admin/r/commercial_spots", label: "Pontos Comerciais", icon: Building2 },
  { to: "/admin/r/banners", label: "Banners", icon: Image },
  { to: "/admin/r/reels", label: "Reels (Vídeos)", icon: Clapperboard },
  { to: "/admin/rewards", label: "Recompensas", icon: Gift },
  { to: "/admin/layout", label: "Personalizar Layout", icon: Palette },
  { to: "/admin/sections", label: "Seções / Layout da Página", icon: ToggleRight },
  { to: "/admin/settings", label: "Configurações", icon: Settings },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 bg-slate-900 text-slate-300 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 flex items-center gap-2 border-b border-white/10">
          <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-bold font-heading">DP</div>
          <div className="font-bold text-white font-heading leading-tight text-sm">Divulga Pocinhos<br /><span className="text-xs font-normal text-slate-400">Painel Admin</span></div>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                data-testid={`admin-nav-${n.to.split("/").pop()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "hover:bg-white/10 text-slate-300"}`
                }
              >
                <Icon className="w-4.5 h-4.5 w-[18px] h-[18px]" /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm hover:bg-white/10 transition-colors">
            <ExternalLink className="w-[18px] h-[18px]" /> Ver site
          </a>
          <button onClick={doLogout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm hover:bg-white/10 transition-colors">
            <LogOut className="w-[18px] h-[18px]" /> Sair
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-border flex items-center gap-3 px-4 h-14">
          <button onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu className="w-6 h-6" /></button>
          <span className="font-bold font-heading">Painel</span>
          <span className="ml-auto text-xs text-muted-foreground truncate max-w-[140px]">{user?.email}</span>
        </header>
        <main className="p-4 sm:p-6 max-w-6xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
